import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const PricingPlans = ({ currentPlan, billingCycle, onPlanSelect, onBillingCycleChange }) => {
  const plans = [
    {
      id: 'free',
      name: 'Artiste',
      price: 0,
      description: 'Pour commencer votre carrière artistique',
      features: [
        'Profil de base',
        '10 messages par mois',
        'Recherche simple',
        'Support communautaire',
        'Galerie de 5 médias'
      ],
      limitations: [
        'Visibilité limitée',
        'Analytics de base uniquement',
        'Pas de priorité dans les résultats'
      ],
      popular: false,
      color: 'border-border',
      bgColor: 'bg-card'
    },
    {
      id: 'professional',
      name: 'Professionnel',
      price: billingCycle === 'monthly' ? 29 : 290,
      description: 'Pour les artistes établis',
      features: [
        'Profil premium avec badge',
        'Messages illimités',
        'Recherche avancée avec filtres',
        'Support prioritaire 24/7',
        'Galerie illimitée',
        'Analytics détaillées',
        'Boost de profil mensuel',
        'Accès aux événements de networking'
      ],
      limitations: [],
      popular: true,
      color: 'border-accent',
      bgColor: 'bg-accent/5'
    },
    {
      id: 'enterprise',
      name: 'Entreprise',
      price: billingCycle === 'monthly' ? 99 : 990,
      description: 'Pour les managers et agences',
      features: [
        'Tout du plan Professionnel',
        'Gestion de multiples artistes',
        'Dashboard manager avancé',
        'API access pour intégrations',
        'Rapports personnalisés',
        'Gestionnaire de compte dédié',
        'Formation et onboarding personnalisé',
        'Invitations aux événements exclusifs VIP'
      ],
      limitations: [],
      popular: false,
      color: 'border-primary',
      bgColor: 'bg-primary/5'
    }
  ];

  const formatPrice = (price) => {
    if (price === 0) return 'Gratuit';
    return `${price}€`;
  };

  const getPlanBadge = (planId) => {
    if (planId === currentPlan) {
      return (
        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-success/10 text-success">
          Plan actuel
        </span>
      );
    }
    return null;
  };

  const getActionButton = (plan) => {
    if (plan?.id === currentPlan) {
      return (
        <Button
          variant="outline"
          fullWidth
          disabled
          iconName="Check"
          iconPosition="left"
          iconSize={16}
        >
          Plan actuel
        </Button>
      );
    }

    if (plan?.id === 'free') {
      return (
        <Button
          variant="outline"
          fullWidth
          iconName="ArrowDown"
          iconPosition="left"
          iconSize={16}
          onClick={() => onPlanSelect(plan?.id)}
        >
          Rétrograder
        </Button>
      );
    }

    return (
      <Button
        variant="default"
        fullWidth
        iconName="ArrowUp"
        iconPosition="left"
        iconSize={16}
        onClick={() => onPlanSelect(plan?.id)}
      >
        {currentPlan === 'free' ? 'Commencer' : 'Passer à ce plan'}
      </Button>
    );
  };

  return (
    <div className="space-y-8">
      {/* Billing Toggle */}
      <div className="flex justify-center">
        <div className="bg-muted p-1 rounded-lg">
          <button
            onClick={() => onBillingCycleChange('monthly')}
            className={`px-6 py-2 rounded-md text-sm font-medium transition-all ${
              billingCycle === 'monthly' ?'bg-background text-foreground shadow-sm' :'text-muted-foreground hover:text-foreground'
            }`}
          >
            Mensuel
          </button>
          <button
            onClick={() => onBillingCycleChange('yearly')}
            className={`px-6 py-2 rounded-md text-sm font-medium transition-all relative ${
              billingCycle === 'yearly' ?'bg-background text-foreground shadow-sm' :'text-muted-foreground hover:text-foreground'
            }`}
          >
            Annuel
            <span className="absolute -top-2 -right-2 bg-accent text-accent-foreground text-xs px-2 py-0.5 rounded-full">
              -17%
            </span>
          </button>
        </div>
      </div>

      {/* Pricing Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {plans?.map((plan) => (
          <div
            key={plan?.id}
            className={`relative rounded-lg border-2 p-8 ${plan?.color} ${plan?.bgColor} shadow-sm ${
              plan?.popular ? 'ring-2 ring-accent/20 scale-105' : ''
            }`}
          >
            {/* Popular Badge */}
            {plan?.popular && (
              <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                <span className="inline-flex items-center px-4 py-1 rounded-full text-sm font-medium bg-accent text-accent-foreground">
                  <Icon name="Star" size={14} className="mr-1" />
                  Le plus populaire
                </span>
              </div>
            )}

            {/* Plan Header */}
            <div className="text-center mb-6">
              <h3 className="text-xl font-semibold text-card-foreground mb-2">
                {plan?.name}
              </h3>
              <p className="text-muted-foreground text-sm mb-4">
                {plan?.description}
              </p>
              <div className="mb-4">
                <span className="text-4xl font-bold text-card-foreground">
                  {formatPrice(plan?.price)}
                </span>
                {plan?.price > 0 && (
                  <span className="text-muted-foreground ml-2">
                    /{billingCycle === 'monthly' ? 'mois' : 'an'}
                  </span>
                )}
              </div>
              {getPlanBadge(plan?.id)}
            </div>

            {/* Features List */}
            <div className="space-y-4 mb-8">
              <div className="space-y-3">
                {plan?.features?.map((feature, index) => (
                  <div key={index} className="flex items-start space-x-3">
                    <Icon name="Check" size={16} className="text-success mt-0.5 flex-shrink-0" />
                    <span className="text-sm text-card-foreground">{feature}</span>
                  </div>
                ))}
              </div>

              {/* Limitations */}
              {plan?.limitations?.length > 0 && (
                <div className="pt-4 border-t border-border">
                  <p className="text-xs text-muted-foreground mb-2">Limitations :</p>
                  <div className="space-y-2">
                    {plan?.limitations?.map((limitation, index) => (
                      <div key={index} className="flex items-start space-x-3">
                        <Icon name="X" size={14} className="text-muted-foreground mt-0.5 flex-shrink-0" />
                        <span className="text-xs text-muted-foreground">{limitation}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Action Button */}
            <div className="space-y-3">
              {getActionButton(plan)}
              
              {plan?.id !== 'free' && (
                <div className="text-center">
                  <button className="text-xs text-muted-foreground hover:text-foreground underline">
                    Voir les détails complets
                  </button>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Additional Information */}
      <div className="bg-muted/50 rounded-lg p-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-center">
          <div className="flex items-center justify-center space-x-3">
            <Icon name="Shield" size={20} className="text-success" />
            <span className="text-sm text-muted-foreground">
              Paiement 100% sécurisé
            </span>
          </div>
          <div className="flex items-center justify-center space-x-3">
            <Icon name="RotateCcw" size={20} className="text-success" />
            <span className="text-sm text-muted-foreground">
              30 jours satisfait ou remboursé
            </span>
          </div>
          <div className="flex items-center justify-center space-x-3">
            <Icon name="Headphones" size={20} className="text-success" />
            <span className="text-sm text-muted-foreground">
              Support client dédié
            </span>
          </div>
        </div>
      </div>

      {/* Enterprise Contact */}
      <div className="bg-primary/5 border border-primary/20 rounded-lg p-6">
        <div className="text-center">
          <h3 className="text-lg font-semibold text-foreground mb-2">
            Besoin d'une solution sur-mesure ?
          </h3>
          <p className="text-muted-foreground mb-4">
            Contactez notre équipe pour discuter de vos besoins spécifiques et obtenir un devis personnalisé.
          </p>
          <Button
            variant="outline"
            iconName="MessageSquare"
            iconPosition="left"
            iconSize={16}
            onClick={() => console.log('Contact sales')}
          >
            Contacter l'équipe commerciale
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PricingPlans;